import { Injectable } from '@angular/core'
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor
} from '@angular/common/http'
import { Observable, from } from 'rxjs'
import { mergeMap, catchError } from 'rxjs/operators'
import { UserAuthService } from '../services/userAuth/user-auth.service'

/**
 * HTTP Interceptor responsible for attaching the user's authentication token to outgoing requests.
 * Also handles automatic logout on receiving a 401 response.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor
{
    /**
     * Initializes the interceptor with access to the authentication service.
     */
    constructor(private authService: UserAuthService)
    {
    }

    /**
     * Intercepts all outgoing HTTP requests to attach the authentication token if available.
     * Also handles special cases like requests that explicitly skip authentication.
     *
     * @param request - The outgoing HTTP request.
     * @param next - The next handler in the chain.
     * @returns Observable emitting the HTTP event stream.
     */
    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>>
    {
        // Skip auth handling if the request explicitly includes the 'noAuth' header
        if (request.headers.has('noAuth'))
        {
            return next.handle(request.clone({
                headers: request.headers.delete('noAuth')
            }))
        }

        // Fetch the latest authentication token and attach it to the request
        return from(this.authService.getToken()).pipe(
            mergeMap(token =>
            {
                // If no token is found (e.g., user logged out), proceed without auth (will likely result in 401)
                if (!token)
                {
                    this.authService.signOut()
                    return next.handle(request)
                }

                // Clone the request to attach the Authorization header
                const authRequest = request.clone({
                    setHeaders: {
                        Authorization: token
                    }
                })

                return next.handle(authRequest)
            }),
            catchError(err =>
            {
                // If a 401 (unauthorized) response is detected, log the user out
                if (err.status === 401)
                {
                    this.authService.signOut()
                }

                // Re-throw the error so the calling code can handle it
                throw err
            })
        )
    }
}